import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import * as XLSX from 'xlsx';
import { Router } from '@angular/router';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AlertService } from '../../../_services/alert.service';
import { InventoryService, StockHistoryFilters, StockHistoryResult, StockMovementRow } from '../../../_services/inventory.service';
import { ViewOrderModalComponent } from '../../sales/view-order-modal/view-order-modal.component';
import { PaginatorPagesDirective } from '../../../../Shared/_directives/paginator-pages.directive';

export interface StockHistoryDialogData {
  productVariantId: number;
  productName: string;
  sku: string;
  label?: string;
  barcode?: string | null;
  image?: string | null;
}

interface TypeLook {
  label: string;
  // What happened, for a sentence: "4 sold", "13 taken out by hand"...
  out: string;
  in: string;
  icon: string;
  cls: string;
}

// How each movement type the backend records is shown: a plain-English phrase
// (for stock going out / coming in), an icon and a tint.
const TYPES: Record<string, TypeLook> = {
  ORDER_PLACED: { label: 'Order placed', out: 'sold', in: 'put back', icon: 'shopping_bag', cls: 'bg-sky-100 text-sky-700' },
  ORDER_CANCELLED: { label: 'Order cancelled', out: 'taken', in: 'put back - order cancelled', icon: 'remove_shopping_cart', cls: 'bg-orange-100 text-orange-700' },
  ORDER_EDITED: { label: 'Order edited', out: 'taken - order edited', in: 'put back - order edited', icon: 'edit_note', cls: 'bg-sky-50 text-sky-600' },
  ORDER_REDEDUCTED: { label: 'Order reinstated', out: 'taken - order reinstated', in: 'put back', icon: 'restore_from_trash', cls: 'bg-sky-100 text-sky-700' },
  STORE_TRANSFER: { label: 'Yard transfer', out: 'sent to the other yard', in: 'received from the other yard', icon: 'swap_horiz', cls: 'bg-indigo-100 text-indigo-700' },
  PURCHASE_RECEIPT: { label: 'Purchase received', out: 'taken', in: 'received from the supplier', icon: 'local_shipping', cls: 'bg-fuchsia-100 text-fuchsia-700' },
  SCRAP: { label: 'Scrapped', out: 'scrapped', in: 'un-scrapped', icon: 'delete_forever', cls: 'bg-red-100 text-red-700' },
  RETURN_RESTOCK: { label: 'Return restocked', out: 'taken', in: 'returned to stock', icon: 'assignment_return', cls: 'bg-green-100 text-green-700' },
  DELIVERY_CANCEL: { label: 'Delivery cancelled', out: 'taken', in: 'put back - delivery cancelled', icon: 'cancel', cls: 'bg-orange-100 text-orange-700' },
  DELIVERY_UNCANCEL: { label: 'Cancel undone', out: 'taken again - cancel undone', in: 'put back', icon: 'undo', cls: 'bg-sky-100 text-sky-700' },
  SUBSTITUTE_OUT: { label: 'Substitute sent', out: 'sent as a substitute', in: 'put back', icon: 'published_with_changes', cls: 'bg-purple-100 text-purple-700' },
  MANUAL_EDIT: { label: 'Changed by hand', out: 'taken out by hand', in: 'added by hand', icon: 'edit', cls: 'bg-amber-100 text-amber-700' },
  STOCK_ADDED: { label: 'Stock added', out: 'taken', in: 'added', icon: 'add_box', cls: 'bg-green-100 text-green-700' },
  ODOO_SYNC: { label: 'Odoo sync', out: 'removed by the Odoo sync', in: 'added by the Odoo sync', icon: 'sync', cls: 'bg-gray-200 text-gray-700' },
  OPENING: { label: 'Opening balance', out: 'opening balance', in: 'opening balance', icon: 'flag', cls: 'bg-gray-100 text-gray-600' },
  REVERSAL: { label: 'Reversal', out: 'taken back', in: 'put back', icon: 'history', cls: 'bg-gray-200 text-gray-700' },
  ADJUSTMENT: { label: 'Adjustment', out: 'taken', in: 'added', icon: 'tune', cls: 'bg-gray-100 text-gray-600' },
};

// One day of movements in the timeline.
interface DayGroup {
  key: string;
  title: string;
  rows: StockMovementRow[];
}

@Component({
    selector: 'app-stock-history',
    imports: [PaginatorPagesDirective, CommonModule, MatDialogModule, MatIconModule, MatTooltipModule, MatPaginatorModule, MatProgressSpinnerModule, DatePipe],
    templateUrl: './stock-history.component.html'
})
export class StockHistoryComponent implements OnInit {
  result: StockHistoryResult | null = null;
  days: DayGroup[] = [];
  loading = true;
  loadError = false;
  exporting = false;
  pageIndex = 0;
  pageSize = 25;
  showGapHelp = false;

  // Filters (asked of the server, so paging stays right).
  warehouse = '';
  direction = '';
  group = '';

  readonly yards = [
    { value: '', label: 'Both yards' },
    { value: 'Kettering', label: 'Kettering' },
    { value: 'Markstey', label: 'Markstey' },
  ];
  readonly groups = [
    { value: '', label: 'Everything', icon: 'list' },
    { value: 'orders', label: 'Orders', icon: 'shopping_bag' },
    { value: 'purchases', label: 'Purchases', icon: 'local_shipping' },
    { value: 'manual', label: 'By hand & counts', icon: 'edit' },
    { value: 'scrapReturns', label: 'Scrap & returns', icon: 'delete_forever' },
    { value: 'transfers', label: 'Transfers', icon: 'swap_horiz' },
  ];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: StockHistoryDialogData,
    private dialogRef: MatDialogRef<StockHistoryComponent>,
    private dialog: MatDialog,
    private router: Router,
    private alertService: AlertService,
    private inventoryService: InventoryService,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  private filters(): StockHistoryFilters {
    return {
      warehouse: this.warehouse || undefined,
      direction: this.direction || undefined,
      group: this.group || undefined,
    };
  }

  get filtersActive(): boolean {
    return !!(this.warehouse || this.direction || this.group);
  }

  load(): void {
    this.loading = true;
    this.loadError = false;
    this.inventoryService.getStockHistory(this.data.productVariantId, this.pageIndex, this.pageSize, this.filters()).subscribe({
      next: (result) => {
        this.result = result;
        this.days = this.groupByDay(result.items);
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.loadError = true;
      },
    });
  }

  private refilter(): void {
    this.pageIndex = 0;
    this.load();
  }

  setYard(value: string): void {
    this.warehouse = value;
    this.refilter();
  }

  setDirection(value: string): void {
    this.direction = this.direction === value ? '' : value;
    this.refilter();
  }

  setGroup(value: string): void {
    this.group = value;
    this.refilter();
  }

  clearFilters(): void {
    this.warehouse = this.direction = this.group = '';
    this.refilter();
  }

  onPage(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.load();
  }

  // ---- The timeline ----

  private groupByDay(items: StockMovementRow[]): DayGroup[] {
    const out: DayGroup[] = [];
    for (const m of items) {
      const d = new Date(m.createdAt);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      let g = out.length ? out[out.length - 1] : undefined;
      if (!g || g.key !== key) {
        g = { key, title: this.dayTitle(d), rows: [] };
        out.push(g);
      }
      g.rows.push(m);
    }
    return out;
  }

  private dayTitle(d: Date): string {
    const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
    const diff = Math.round((startOfDay(new Date()) - startOfDay(d)) / 86400000);
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Yesterday';
    return new DatePipe('en').transform(d, diff < 7 ? 'EEEE d MMMM' : 'd MMMM y') ?? '';
  }

  private look(type: string): TypeLook {
    return TYPES[type] ?? { label: type, out: 'taken', in: 'added', icon: 'tune', cls: 'bg-gray-100 text-gray-600' };
  }

  typeLabel(m: StockMovementRow): string {
    return this.look(m.movementType).label;
  }

  icon(m: StockMovementRow): string {
    return this.look(m.movementType).icon;
  }

  // The coloured circle takes the movement type's tint.
  tint(m: StockMovementRow): string {
    return this.look(m.movementType).cls;
  }

  // "4 sold", "13 taken out by hand", "3 put back - order cancelled".
  sentence(m: StockMovementRow): string {
    const look = this.look(m.movementType);
    if (m.movementType === 'OPENING') return `Started at ${m.balanceAfter}`;
    const n = Math.abs(m.quantityChange);
    return `${n} ${m.quantityChange < 0 ? look.out : look.in}`;
  }

  // "Edited on the Inventory page: Stock count" -> the reason; anything else as written.
  reason(m: StockMovementRow): string {
    const notes = (m.notes ?? '').trim();
    const match = notes.match(/^Edited on the Inventory page:\s*(.+)$/i);
    if (match) return match[1];
    if (/^Edited on the Inventory page$/i.test(notes)) return '';
    if (/^Balance when stock history started$/i.test(notes)) return '';
    return notes;
  }

  changeText(m: StockMovementRow): string {
    return `${m.quantityChange > 0 ? '+' : ''}${m.quantityChange}`;
  }

  ago(date: string): string {
    const mins = Math.floor((Date.now() - new Date(date).getTime()) / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins} min ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    const days = Math.floor(hours / 24);
    if (days < 31) return `${days} day${days === 1 ? '' : 's'} ago`;
    const months = Math.round(days / 30);
    return months < 12 ? `${months} month${months === 1 ? '' : 's'} ago` : `${Math.round(days / 365)} year${days < 548 ? '' : 's'} ago`;
  }

  who(m: StockMovementRow): string {
    return (m.createdBy ?? '').split('@')[0] || 'the system';
  }

  // ---- Where it came from ----

  canOpen(m: StockMovementRow): boolean {
    return !!m.referenceId && (m.referenceType === 'ORDER' || m.referenceType === 'PURCHASE_ORDER');
  }

  openReference(m: StockMovementRow): void {
    if (!m.referenceId) return;
    if (m.referenceType === 'ORDER') {
      this.dialog.open(ViewOrderModalComponent, { width: '50%', height: '700px', data: { type: 'VIEW', orderId: m.referenceId } });
    } else if (m.referenceType === 'PURCHASE_ORDER') {
      this.dialogRef.close();
      this.router.navigate(['/purchases/edit', m.referenceId]);
    }
  }

  // ---- The check that history matches live stock ----

  // Live stock minus what the recorded movements add up to, per yard.
  get gapKettering(): number {
    return this.result ? this.result.actual.Kettering - this.result.ledger.Kettering : 0;
  }

  get gapMarkstey(): number {
    return this.result ? this.result.actual.Markstey - this.result.ledger.Markstey : 0;
  }

  get hasGap(): boolean {
    return this.gapKettering !== 0 || this.gapMarkstey !== 0;
  }

  // ---- A small picture of the total over the rows on screen ----

  // Oldest to newest, so the line reads left to right.
  get trend(): { points: string; min: number; max: number } | null {
    const rows = [...(this.result?.items ?? [])].reverse();
    if (rows.length < 3) return null;
    const values = rows.map((r) => r.totalAfter);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const span = max - min || 1;
    const points = values.map((v, i) => `${(i / (values.length - 1)) * 120},${28 - ((v - min) / span) * 24}`).join(' ');
    return { points, min, max };
  }

  // ---- Download ----

  exportHistory(): void {
    if (this.exporting || !this.result) return;
    this.exporting = true;
    this.inventoryService.getStockHistory(this.data.productVariantId, 0, 5000, this.filters()).subscribe({
      next: (all) => {
        const rows = all.items.map((m) => ({
          'When': new Date(m.createdAt),
          'Yard': m.warehouse,
          'Change': m.quantityChange,
          'Balance at yard': m.balanceAfter,
          'Total on hand': m.totalAfter,
          'What happened': this.typeLabel(m),
          'Reference': m.referenceLabel ?? '',
          'Reason / notes': this.reason(m),
          'By': m.createdBy ?? '',
        }));
        const ws = XLSX.utils.json_to_sheet(rows);
        ws['!cols'] = [20, 11, 9, 15, 14, 20, 20, 44, 28].map((wch) => ({ wch }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Stock history');
        XLSX.writeFile(wb, `stock-history-${this.data.sku || this.data.productVariantId}-${new Date().toISOString().slice(0, 10)}.xlsx`);
        this.exporting = false;
      },
      error: () => {
        this.exporting = false;
        this.alertService.alert('Could not download the history', 'error');
      },
    });
  }

  copySku(): void {
    if (!this.data.sku) return;
    navigator.clipboard?.writeText(this.data.sku).then(() => this.alertService.alert('SKU copied', 'success'));
  }

  close(): void {
    this.dialogRef.close();
  }
}
